import { createReducer, on } from '@ngrx/store';
import { TimeEntry, Expense, Invoice, MatterBillingSummary } from '../../shared/models/billing.model';
import * as BillingActions from './billing.actions';

export interface BillingState {
  timeEntries: TimeEntry[];
  timeEntriesTotal: number;
  expenses: Expense[];
  expensesTotal: number;
  invoices: Invoice[];
  invoicesTotal: number;
  matterBillingSummary: MatterBillingSummary | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

const initialState: BillingState = {
  timeEntries: [],
  timeEntriesTotal: 0,
  expenses: [],
  expensesTotal: 0,
  invoices: [],
  invoicesTotal: 0,
  matterBillingSummary: null,
  isLoading: false,
  isSaving: false,
  error: null,
};

export const billingReducer = createReducer(
  initialState,

  // Loading states
  on(
    BillingActions.loadTimeEntries,
    BillingActions.loadExpenses,
    BillingActions.loadInvoices,
    BillingActions.loadMatterBillingSummary,
    (state) => ({ ...state, isLoading: true, error: null })
  ),
  on(
    BillingActions.createTimeEntry,
    BillingActions.updateTimeEntry,
    BillingActions.deleteTimeEntry,
    BillingActions.createExpense,
    BillingActions.updateExpense,
    BillingActions.deleteExpense,
    BillingActions.createInvoice,
    BillingActions.updateInvoice,
    (state) => ({ ...state, isSaving: true, error: null })
  ),

  // Time Entry successes
  on(BillingActions.loadTimeEntriesSuccess, (state, { response }) => ({
    ...state,
    timeEntries: response.items,
    timeEntriesTotal: response.total,
    isLoading: false,
  })),
  on(BillingActions.createTimeEntrySuccess, (state, { entry }) => ({
    ...state,
    timeEntries: [entry, ...state.timeEntries],
    timeEntriesTotal: state.timeEntriesTotal + 1,
    isSaving: false,
  })),
  on(BillingActions.updateTimeEntrySuccess, (state, { entry }) => ({
    ...state,
    timeEntries: state.timeEntries.map(te => te._id === entry._id ? entry : te),
    isSaving: false,
  })),
  on(BillingActions.deleteTimeEntrySuccess, (state, { id }) => ({
    ...state,
    timeEntries: state.timeEntries.filter(te => te._id !== id),
    timeEntriesTotal: Math.max(0, state.timeEntriesTotal - 1),
    isSaving: false,
  })),

  // Expense successes
  on(BillingActions.loadExpensesSuccess, (state, { response }) => ({
    ...state,
    expenses: response.items,
    expensesTotal: response.total,
    isLoading: false,
  })),
  on(BillingActions.createExpenseSuccess, (state, { expense }) => ({
    ...state,
    expenses: [expense, ...state.expenses],
    expensesTotal: state.expensesTotal + 1,
    isSaving: false,
  })),
  on(BillingActions.updateExpenseSuccess, (state, { expense }) => ({
    ...state,
    expenses: state.expenses.map(ex => ex._id === expense._id ? expense : ex),
    isSaving: false,
  })),
  on(BillingActions.deleteExpenseSuccess, (state, { id }) => ({
    ...state,
    expenses: state.expenses.filter(ex => ex._id !== id),
    expensesTotal: Math.max(0, state.expensesTotal - 1),
    isSaving: false,
  })),

  // Invoice successes
  on(BillingActions.loadInvoicesSuccess, (state, { response }) => ({
    ...state,
    invoices: response.items,
    invoicesTotal: response.total,
    isLoading: false,
  })),
  on(BillingActions.createInvoiceSuccess, (state, { invoice }) => ({
    ...state,
    invoices: [invoice, ...state.invoices],
    invoicesTotal: state.invoicesTotal + 1,
    isSaving: false,
  })),
  on(BillingActions.updateInvoiceSuccess, (state, { invoice }) => ({
    ...state,
    invoices: state.invoices.map(inv => inv._id === invoice._id ? invoice : inv),
    isSaving: false,
  })),

  // Matter billing summary
  on(BillingActions.loadMatterBillingSummarySuccess, (state, { summary }) => ({
    ...state,
    matterBillingSummary: summary,
    isLoading: false,
  })),

  // Failures
  on(
    BillingActions.loadTimeEntriesFailure,
    BillingActions.createTimeEntryFailure,
    BillingActions.updateTimeEntryFailure,
    BillingActions.deleteTimeEntryFailure,
    BillingActions.loadExpensesFailure,
    BillingActions.createExpenseFailure,
    BillingActions.updateExpenseFailure,
    BillingActions.deleteExpenseFailure,
    BillingActions.loadInvoicesFailure,
    BillingActions.createInvoiceFailure,
    BillingActions.updateInvoiceFailure,
    BillingActions.loadMatterBillingSummaryFailure,
    (state, { error }) => ({ ...state, isLoading: false, isSaving: false, error })
  ),

  on(BillingActions.clearBillingError, (state) => ({ ...state, error: null })),
);
